import { ecrireJson, lireJson, type Resultat } from './client';

/** Statuts d'intervention — liste fermée, identique à celle du serveur. */
export type StatutIntervention = 'planifiee' | 'en-cours' | 'terminee' | 'annulee';

export interface Intervention {
  readonly id: string;
  readonly date: string;
  /** Identifiant du client : c'est lui qui rattache la ligne, pas le nom. */
  readonly clientId: string;
  readonly client: string;
  readonly typeIntervention: string;
  readonly lieu: string;
  readonly statut: StatutIntervention;
  /** Heure de début `HH:MM`, absente quand l'intervention n'a pas de créneau. */
  readonly heureDebut?: string | null;
  readonly dureeMinutes?: number;
  /** Heure de fin CALCULÉE par le serveur : l'écran n'additionne jamais. */
  readonly fin?: string | null;
  /** « 1 heure », « 1 h 30 » — décidé par le serveur. */
  readonly dureeLisible?: string;
  readonly technicienId?: string | null;
  readonly technicien?: string | null;
  readonly notes?: string | null;
  /** Vrai si la date est passée et l'intervention encore ouverte — décidé serveur. */
  readonly enRetard?: boolean;
  /** Pas de la récurrence en mois ; 0 = ponctuelle. */
  readonly recurrenceMois?: number;
  /** « Tous les ans », « Ponctuelle » — écrit par le serveur. */
  readonly recurrenceLibelle?: string;
  readonly serieId?: string | null;
  /** Pourquoi l'intervention a été annulée — obligatoire sur une annulation. */
  readonly motifAnnulation?: string | null;
}

/**
 * Réponse d'une modification : l'intervention, et — quand le serveur vient de créer
 * le contrôle suivant — cette occurrence. L'écran l'annonce, il ne la devine pas.
 */
export type InterventionModifiee = Intervention & {
  readonly prochaineOccurrence?: Intervention;
};

/** Ce que le modal envoie pour planifier une intervention. */
export interface Planification {
  readonly clientId: string;
  readonly date: string;
  readonly heureDebut: string;
  readonly typeIntervention: string;
  readonly lieu: string;
  readonly dureeMinutes: number;
  /** `''` = « à assigner plus tard » : le serveur n'assigne personne. */
  readonly technicienId: string;
  readonly notes: string;
  /** Récurrence en mois ; 0 = ponctuelle. */
  readonly recurrenceMois: number;
}

/** Les listes ET les défauts du modal, servis par le serveur. */
export interface ReferencesIntervention {
  readonly clients: ReadonlyArray<{
    readonly id: string;
    readonly nom: string;
    readonly ville: string;
    /** Adresse du client, mise en forme par le serveur — propose le lieu. */
    readonly lieuSuggere?: string;
  }>;
  readonly sites: readonly string[];
  readonly types: readonly string[];
  readonly techniciens: ReadonlyArray<{ readonly id: string; readonly nom: string }>;
  readonly durees: ReadonlyArray<{ readonly minutes: number; readonly libelle: string }>;
  readonly recurrences?: ReadonlyArray<{ readonly mois: number; readonly libelle: string }>;
  readonly defauts: {
    readonly date: string;
    readonly heure: string;
    readonly dureeMinutes: number;
  };
  readonly aujourdHui?: string;
}

/** Les quatre indicateurs de l'écran, servis par le serveur. */
export interface CompteursInterventions {
  readonly planifiees: number;
  readonly enCours: number;
  readonly termineesMois: number;
  readonly enRetard: number;
}

/** Tris possibles pour la liste — liste fermée, identique au serveur. */
export type TriInterventions = 'date' | 'client';
export type SensTri = 'asc' | 'desc';

export interface PageInterventions {
  readonly items: readonly Intervention[];
  readonly total: number;
  readonly page: number;
  readonly taille: number;
  readonly pages: number;
  /** Indicateurs de TOUT le portefeuille (pas seulement la page). */
  readonly compteurs?: CompteursInterventions;
  readonly tris?: ReadonlyArray<{ readonly cle: TriInterventions; readonly libelle: string }>;
  readonly tri?: { readonly cle: TriInterventions; readonly sens: SensTri };
  readonly aujourdHui?: string;
  readonly mois?: string;
}

/** Un jour du calendrier : ce que le serveur a compté. */
export interface JourCalendrier {
  readonly date: string;
  readonly nombre: number;
  readonly enRetard: number;
  readonly statuts: readonly StatutIntervention[];
}

export interface MoisVoisin {
  readonly mois: string;
  readonly libelle: string;
}

export interface CalendrierMois {
  readonly mois: string;
  /** « octobre 2026 » — écrit par le serveur, pas par l'écran. */
  readonly libelle?: string;
  readonly debut: string;
  readonly fin: string;
  readonly total: number;
  readonly jours: readonly JourCalendrier[];
  /** Vrai quand le mois affiché est celui d'aujourd'hui. */
  readonly courant?: boolean;
  readonly precedent?: MoisVoisin | null;
  readonly suivant?: MoisVoisin | null;
  readonly aujourdHui?: string;
}

/** Sens d'une variation, décidé par le serveur. */
export type SensTendance = 'hausse' | 'baisse' | 'stable' | 'aucune';

/** Ce que la variation signifie — le serveur tranche, l'écran colore. */
export type TonaliteTendance = 'positive' | 'negative' | 'neutre';

export interface Tendance {
  readonly sens: SensTendance;
  readonly tonalite: TonaliteTendance;
}

/** Une ligne de compteur : le nombre, sa part, et la comparaison. */
export interface CompteurStatut {
  readonly statut: StatutIntervention;
  readonly libelle: string;
  readonly nombre: number;
  /** Part du total en pourcentage entier ; la somme vaut 100 (décidé serveur). */
  readonly pourcentage: number;
  /** Période précédente ; `null` quand il n'y a rien à comparer (« tout »). */
  readonly precedent: number | null;
  /** Écart signé ; `null` quand il n'y a pas de comparaison possible. */
  readonly delta: number | null;
  readonly tendance: Tendance;
}

/** Les périodes proposées par le serveur. */
export interface PeriodeTableau {
  readonly cle: string;
  readonly libelle: string;
}

export interface Resume {
  readonly version: string;
  readonly aujourdHui: string;
  readonly periode: {
    readonly cle: string;
    readonly libelle: string;
    readonly debut: string | null;
    readonly fin: string | null;
  };
  readonly periodes: readonly PeriodeTableau[];
  /** `false` : pas de période précédente, donc aucune flèche à dessiner. */
  readonly comparaison: boolean;
  readonly total: number;
  readonly parStatut: readonly CompteurStatut[];
  readonly retards: {
    /** Tous les retards, quel qu'en soit le nombre. */
    readonly total: number;
    /** Le détail borné par le serveur (les plus anciens). */
    readonly items: readonly Intervention[];
    /** Retards non détaillés. */
    readonly autres: number;
    /** Clients distincts cités par le serveur, déjà dédoublonnés. */
    readonly clients: readonly string[];
    readonly clientsTotal: number;
    /** Où aller pour les traiter : la liste filtrée sur le MÊME critère. */
    readonly lien: string;
  };
  readonly prochaine: Intervention | null;
  /** Lien vers l'intervention « prochaine », décidé par le serveur. */
  readonly lienProchaine: string | null;
}

/** Ce que l'écran envoie pour créer une intervention. */
export interface SaisieIntervention {
  readonly clientId: string;
  readonly date: string;
  readonly typeIntervention: string;
  readonly lieu: string;
  readonly statut: StatutIntervention;
}

/**
 * Modification partielle : un champ absent n'est pas touché.
 *
 * Les champs EFFAÇABLES (heure, technicien, notes) acceptent une chaîne vide :
 * c'est ainsi qu'on retire un créneau ou une assignation.
 */
export interface ChangementIntervention {
  readonly clientId?: string;
  readonly date?: string;
  readonly typeIntervention?: string;
  readonly lieu?: string;
  readonly statut?: StatutIntervention;
  readonly heureDebut?: string;
  readonly dureeMinutes?: number;
  readonly technicienId?: string;
  readonly notes?: string;
  /** Exigé par le serveur quand `statut` devient `annulee`. */
  readonly motifAnnulation?: string;
}

export interface Criteres {
  readonly recherche?: string;
  readonly statut?: StatutIntervention | '';
  /** `true` : seulement les interventions en retard (décidé serveur). */
  readonly retard?: boolean;
  /** Fenêtre glissante : `semaine` · `mois` · `annee` · `tout`. */
  readonly periode?: string;
  /** Jour exact (`AAAA-MM-JJ`), posé par un clic dans le calendrier. */
  readonly date?: string;
  readonly tri?: TriInterventions;
  readonly sens?: SensTri;
  readonly page?: number;
  readonly taille?: number;
}

/**
 * Construit l'URL de la liste. Le tri, le comptage et la pagination sont faits
 * par le serveur : l'écran n'envoie que des critères.
 */
export function urlInterventions(criteres: Criteres = {}): string {
  const parametres = new URLSearchParams();
  const recherche = criteres.recherche?.trim();
  if (recherche) parametres.set('recherche', recherche);
  if (criteres.statut) parametres.set('statut', criteres.statut);
  if (criteres.retard) parametres.set('retard', '1');
  if (criteres.periode && criteres.periode !== 'tout') parametres.set('periode', criteres.periode);
  if (criteres.date) parametres.set('date', criteres.date);
  if (criteres.tri && criteres.tri !== 'date') parametres.set('tri', criteres.tri);
  // Le sens décroissant est le défaut du serveur pour cette liste.
  if (criteres.sens === 'asc') parametres.set('sens', 'asc');
  if (criteres.page && criteres.page > 1) parametres.set('page', String(criteres.page));
  if (criteres.taille) parametres.set('taille', String(criteres.taille));

  const requete = parametres.toString();
  return requete === '' ? '/api/interventions' : `/api/interventions?${requete}`;
}

export function listerInterventions(
  criteres: Criteres = {},
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<PageInterventions>> {
  return lireJson<PageInterventions>(urlInterventions(criteres), options);
}

/**
 * Détail d'UNE intervention, servi par le serveur.
 *
 * L'écran ne recopie plus la ligne de la liste : le jour où la liste renverra des
 * lignes partielles, le détail doit encore être complet.
 */
export function lireIntervention(
  id: string,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<Intervention>> {
  return lireJson<Intervention>(`/api/interventions/${encodeURIComponent(id)}`, options);
}

/** Construit l'URL du tableau de bord : la période FILTRE côté serveur. */
export function urlResume(periode?: string): string {
  return periode
    ? `/api/tableau-de-bord?periode=${encodeURIComponent(periode)}`
    : '/api/tableau-de-bord';
}

export function lireResume(
  options: {
    periode?: string;
    fetchImpl?: typeof fetch;
    signal?: AbortSignal;
  } = {},
): Promise<Resultat<Resume>> {
  const { periode, ...reste } = options;
  return lireJson<Resume>(urlResume(periode), reste);
}

/** Crée une intervention rattachée à un client DU portefeuille. */
/**
 * Les références du modal « Planifier une intervention ».
 *
 * L'écran n'invente aucune liste : clients, sites, types, techniciens, durées et
 * valeurs par défaut viennent tous d'ici, en une seule requête.
 */
export function lireReferencesIntervention(
  options: {
    fetchImpl?: typeof fetch;
    signal?: AbortSignal;
    /** Jour pré-rempli (clic sur une case du calendrier) — validé par le serveur. */
    date?: string;
  } = {},
): Promise<Resultat<ReferencesIntervention>> {
  const requete = options.date ? `?date=${encodeURIComponent(options.date)}` : '';
  return lireJson<ReferencesIntervention>(`/api/interventions/references${requete}`, options);
}

/** Une fiche technicien SANS compte : le prestataire décrit son équipe. */
export function creerTechnicien(
  fiche: { nom: string; email: string; telephone?: string; role?: string },
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<{ id: string; nom: string }>> {
  return ecrireJson<{ id: string; nom: string }>('/api/techniciens', 'POST', fiche, options);
}

/** Replanifie une intervention annulée : le serveur copie la fiche à la nouvelle date. */
export function relancerIntervention(
  id: string,
  date: string,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<Intervention>> {
  return ecrireJson<Intervention>(
    `/api/interventions/${encodeURIComponent(id)}/relance`,
    'POST',
    { date },
    options,
  );
}

/** Planifie une intervention : le serveur valide, calcule la fin et vérifie l'agenda. */
export function planifierIntervention(
  planification: Planification,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<Intervention>> {
  return ecrireJson<Intervention>(
    '/api/interventions',
    'POST',
    { ...planification, statut: 'planifiee' },
    options,
  );
}

export function creerIntervention(
  saisie: SaisieIntervention,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<Intervention>> {
  return ecrireJson<Intervention>('/api/interventions', 'POST', saisie, options);
}

/** Modifie une intervention (le plus souvent son statut). */
export function modifierIntervention(
  id: string,
  changement: ChangementIntervention,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<InterventionModifiee>> {
  return ecrireJson<InterventionModifiee>(
    `/api/interventions/${encodeURIComponent(id)}`,
    'PATCH',
    changement,
    options,
  );
}

/** L'export suit les mêmes filtres que la liste, sans la pagination. */
export function urlExportInterventions(criteres: Criteres = {}): string {
  const url = urlInterventions({ ...criteres, page: undefined, taille: undefined });
  const requete = url.includes('?') ? url.slice(url.indexOf('?')) : '';
  return `/api/interventions/export.csv${requete}`;
}

/** Le calendrier d'un mois (« AAAA-MM ») : un compte par jour, décidé en SQL. */
export function lireCalendrier(
  mois?: string,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<CalendrierMois>> {
  const requete = mois ? `?mois=${encodeURIComponent(mois)}` : '';
  return lireJson<CalendrierMois>(`/api/interventions/calendrier${requete}`, options);
}

/**
 * Change le statut de plusieurs interventions. Le serveur ne touche que les
 * lignes du compte et rend le nombre RÉELLEMENT modifié.
 */
export function changerStatuts(
  ids: readonly string[],
  statut: StatutIntervention,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<{ modifiees: number; statut: StatutIntervention }>> {
  return ecrireJson('/api/interventions/statuts', 'POST', { ids, statut }, options);
}

/** Une journée de charge, telle que le serveur l'a additionnée. */
export interface ChargeJour {
  readonly date: string;
  readonly minutes: number;
  readonly interventions: number;
}

export interface ChargeTechnicien {
  readonly id?: string | null;
  readonly nom: string;
  readonly minutes: number;
  readonly interventions: number;
  readonly jours: readonly ChargeJour[];
  /** « 2 h 15 » — écrit par le serveur. */
  readonly minutesLisible: string;
}

export interface ChargeSemaine {
  readonly debut: string;
  readonly fin: string;
  readonly techniciens: readonly ChargeTechnicien[];
  readonly nonAssignees: ChargeJour;
  readonly totalMinutes: number;
  readonly totalLisible: string;
}

export interface ReponseCharge {
  readonly precedente: string;
  readonly suivante: string;
  readonly charge: ChargeSemaine;
  readonly aujourdHui?: string;
}

/** La charge de l'équipe sur une semaine : tout est additionné par le serveur. */
export function lireCharge(
  semaine?: string,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<ReponseCharge>> {
  const requete = semaine ? `?semaine=${encodeURIComponent(semaine)}` : '';
  return lireJson<ReponseCharge>(`/api/interventions/charge${requete}`, options);
}
